import Workspace from 'iam/models/Workspace'
import omit from 'lodash/omit'
import uniq from 'lodash/uniq'
import { action, computed, observable, makeObservable } from 'mobx'
import {
  combineLatest,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
} from 'rxjs'
import {
  delay,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  takeWhile,
  withLatestFrom,
} from 'rxjs/operators'
import { CursorPaged, CursorPageQuery } from 'types/definitions'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { fromStream, toStream, tapAction } from 'utils/mobx-util'
import { areDeepEqual } from 'utils/object-util'
import { shareLatest, withCancellation, unpair } from 'utils/rx-util'
import Opportunity from './Opportunity'
import { OpportunityEvent } from './opportunity-events'
import {
  ListOpportunitiesQuery,
  OpportunitySortingField,
} from './OpportunityStore'
import { ListOpportunitiesDelegate } from './ports'

/**
 * Implements opportunity searching.
 */
export default class OpportunitiesSearchViewModel {
  /**
   * Observable subscription used for disposing on deactivate.
   */
  private subscription = new Subscription()

  /**
   * Used to trigger a search immediately with the current filter.
   */
  private searchTrigger = new Subject()

  /**
   * Search text changes.
   */
  private searchTextChange = new Subject<SearchTextChange>()

  /**
   * Backing field for the last search.
   */
  @observable.ref private _lastSearch: OpportunitySearchResult = {
    opportunities: [],
    searchFilter: OpportunitySearchFilter.none,
    total: 0,
    filtersApplied: false,
  }

  /**
   * Whether the control is visible.
   */
  @observable visible = true

  /**
   * Workspace filter.
   */
  @observable workspaceFilter: OpportunityWorkspaceFilter = {
    type: 'AllWorkspaces',
  }

  /**
   * Sorting.
   */
  @observable sorting: OpportunitySort = { type: 'Default' }

  /**
   * Stream for workspace filter values. Emits on subscribe.
   */
  readonly workspaceFilter$ = toStream(() => this.workspaceFilter, true)

  /**
   * Opportunities scroller.
   */
  scroller = new InfiniteScrollProvider<Opportunity, OpportunitySearchFilter>({
    limit: 20,
    // We trigger the initial fetch ourselves. If this was false,
    // we would see 2 initial fetches.
    startFetchTaskAsPending: true,
    fetchItems: (paging, _, filter) =>
      this._fetchOpportunities(toListOpportunitiesQuery(paging, filter)),
  })

  /**
   * Search filter changes.
   */
  private searchFilter$ = combineLatest([
    // Combine the search text changes...
    this.searchTextChange.pipe(
      switchMap((change) =>
        // Apply the delay if debouncing
        change.scheduling === ChangeScheduling.Debounced
          ? of(change.value).pipe(delay(300))
          : of(change.value),
      ),
      startWith(''),
    ),
    // ...with the workspace filter changes
    toStream(() => this.workspaceFilter, true),
    // ...and the sort
    toStream(() => this.sorting, true),
  ]).pipe(
    // Map what we've combined to a Search Filter.
    map(
      ([searchText, workspaceFilter, sort]): OpportunitySearchFilter => ({
        searchText,
        sort,
        workspace:
          workspaceFilter.type === 'AllWorkspaces'
            ? Workspace.none
            : workspaceFilter.workspace,
      }),
    ),
    // Make sure to only subscribe once, share the latest value.
    shareLatest(),
  )

  /**
   * Searches observable. Each emitted value is the search result of
   * a completed query.
   */
  searches$ = merge(
    // When the search filter changes
    this.searchFilter$,
    // Or when we explicitly request a search for the current filter (can be used to skip debouncing)
    this.searchTrigger.pipe(
      // Since searchFilter$ is using replay, it will emit the last
      // search filter immediately on subscribe. So smart. ＼（＾ ＾）／
      switchMap(() => this.searchFilter$),
    ),
  ).pipe(
    // Start a non-filtered search immediately.
    startWith(OpportunitySearchFilter.none),
    // Ignore emissions with the same search filter.
    distinctUntilChanged<OpportunitySearchFilter>(
      OpportunitySearchFilter.equals,
    ),
    // Start a search, return the filter that was used.
    switchMap(
      (query): Observable<OpportunitySearchResult> =>
        withCancellation(async (ct) => {
          let result = await this.scroller.fetch(query)
          // Keep loading more until we get something back that isn't being filtered out
          // on the client. If we don't do this here, we potentially display an empty state
          // which does not trigger infinite scroll.
          while (this.opportunities.length === 0 && !this.scroller.isDone) {
            ct.throwIfCancelled()
            result = await this.scroller.more()
          }
          return {
            searchFilter: query,
            filtersApplied: !OpportunitySearchFilter.isNone(query),
            total: result?.total || this.opportunities.length,
            opportunities: this.opportunities,
          }
        }),
    ),
    // Make sure to only subscribe once.
    shareLatest(),
  )

  /**
   * Constructor.
   */
  constructor(
    private opportunityFetcher: ListOpportunitiesDelegate,
    private events$: Observable<OpportunityEvent>,
  ) {
    makeObservable(this)
  }

  /**
   * The internal opportunities list.
   */
  @computed
  get opportunities() {
    // Due to our insertion and removal smarts, it's possible that we'll
    // observe duplicate opportunities in the scroller's items.
    // For example, if we get a realtime event for an opportunity that appears
    // later when scrolling to load more, it can appear again.
    // Since opportunities are referentially equivalent, we can deduplicate them easily.
    return uniq(this.scroller.items)
  }

  /**
   * Search text computed getter.
   */
  @computed
  get searchText() {
    return this.getSearchText()
  }

  /**
   * Last search computed getter.
   */
  @computed
  get lastSearch() {
    return this._lastSearch
  }

  /**
   * Gets the current search state.
   */
  @computed
  get searchState(): SearchState {
    if (this.getIsFirstFetchRunning()) {
      return SearchState.InitialSearchRunning
    }

    if (this.lastSearch.opportunities.length === 0 && this.scroller.isDone) {
      return this.lastSearch.filtersApplied
        ? SearchState.EmptyButFilterApplied
        : SearchState.CompletelyEmpty
    }

    return SearchState.Some
  }

  /**
   * Activates the view model.
   */
  activate() {
    // Only react to opportunity insertions/updates if they match our workspace filter,
    // and only if we are not doing a text search.
    // This means that we would potentially miss events if searching by text, but that's
    // not really something most people do for opportunities.
    const events$ = this.events$.pipe(
      withLatestFrom(this.searches$),
      filter(([event, search]) => {
        // Always handle opportunity removed events.
        if (event.type === 'OpportunityRemoved') {
          return true
        }

        // If we are not filtering at all, handle the event.
        if (OpportunitySearchFilter.isNone(search.searchFilter)) {
          return true
        }

        // If there's search text, don't handle the event.
        if (search.searchFilter.searchText) {
          return false
        }

        // If the workspace of the opportunity matches that of the filter, handle the event.
        return search.searchFilter.workspace === event.opportunity.workspace
      }),
      // We only care about the event, not the search.
      unpair(),
    )

    this.subscription = merge(
      // Search requests.
      this.searches$.pipe(
        // Can't use fromStream because it will subscribe immediately,
        // which we don't want.
        tapAction((search) => (this._lastSearch = search)),
      ),

      // When opportunities are added/updated, make sure they are in the list
      events$.pipe(
        filter((e) => e.type !== 'OpportunityRemoved'),
        map((e) => e.opportunity),
        filter((op) => !this.scroller.items.includes(op)),
        tapAction((op) => this.scroller.items.push(op)),
      ),

      // When opportunities are removed, remove them from the list.
      events$.pipe(
        filter((e) => e.type === 'OpportunityRemoved'),
        map((e) => e.opportunity),
        filter((op) => this.scroller.items.includes(op)),
        tapAction((op) =>
          this.scroller.items.splice(this.scroller.items.indexOf(op), 1),
        ),
      ),
    ).subscribe()
  }

  /**
   * Called when the view model is not being actively used.
   */
  deactivate() {
    this.subscription.unsubscribe()
  }

  /**
   * Sets the search text.
   *
   * @param value
   * @param scheduling
   */
  @action.bound
  setSearchText(value: string, scheduling = ChangeScheduling.Debounced) {
    this.searchTextChange.next({
      value,
      scheduling,
    })
  }

  /**
   * Selects the workspace to filter by.
   */
  @action.bound
  selectWorkspaceForFiltering(workspace: Workspace) {
    this.workspaceFilter =
      workspace === Workspace.none
        ? { type: 'AllWorkspaces' }
        : { workspace, type: 'SpecificWorkspace' }
  }

  /**
   * Sets the opportunity sort.
   *
   * @param sortMethod
   */
  @action.bound
  setSortMethod(sortMethod: OpportunitySort) {
    this.sorting = sortMethod
  }

  /**
   * Sets the opportunity sort.
   *
   * @param field
   */
  @action.bound
  setSortField(field: OpportunitySortingField) {
    this.setSortMethod({
      field,
      type: 'Field',
      direction:
        this.sorting.type === 'Field'
          ? this.sorting.direction
          : SortDirection.Ascending,
    })
  }

  /**
   * Sets the opportunity sort.
   *
   * @param direction
   */
  @action.bound
  setSortDirection(direction: SortDirection) {
    if (this.sorting.type !== 'Field') {
      return
    }

    this.setSortMethod({
      direction,
      field: this.sorting.field,
      type: 'Field',
    })
  }

  /**
   * Clears the search text.
   */
  @action.bound
  clearSearchText() {
    this.setSearchText('', ChangeScheduling.Immediate)
  }

  /**
   * Clears the filters.
   */
  @action.bound
  clearFilters() {
    this.setSearchText('', ChangeScheduling.Immediate)
    this.selectWorkspaceForFiltering(Workspace.none)
  }

  /**
   * Fetch opportunities.
   *
   * @param params
   */
  private async _fetchOpportunities(params: ListOpportunitiesQuery) {
    try {
      return await this.opportunityFetcher(params)
    } catch (error) {
      console.error(error)
      return CursorPaged.empty
    }
  }

  /**
   * The search text as a computed, derived from the search text change stream.
   */
  private getSearchText = fromStream(
    '',
    this.searchTextChange.pipe(map((x) => x.value)),
  )

  /**
   * Subscribes to the pending state of the fetch task.
   * Once it flips to `false`, stop.
   */
  private getIsFirstFetchRunning = fromStream(
    true,
    toStream(() => this.scroller.fetch.pending, true).pipe(
      takeWhile(Boolean, true),
    ),
  )
}

/**
 * Creates a `ListOpportunities` from the paging and filter.
 *
 * @param paging
 * @param filter
 */
function toListOpportunitiesQuery(
  paging: CursorPageQuery,
  filter: OpportunitySearchFilter | undefined,
): ListOpportunitiesQuery {
  const orderBy: ListOpportunitiesQuery['orderBy'] =
    filter?.sort.type === 'Field'
      ? {
          [filter.sort.field]:
            filter.sort.direction === SortDirection.Descending ? 'desc' : 'asc',
        }
      : // Sort by last modified
        {
          modified: 'desc',
        }

  return {
    orderBy,
    searchText: filter?.searchText,
    workspaceId: filter?.workspace.id || undefined,
    limit: paging.limit,
    after: paging.after,
  }
}

/**
 * Workspace filter for Opportunities.
 */
export type OpportunityWorkspaceFilter = AllWorkspaces | SpecificWorkspace

/**
 * Opportunities in all workspaces.
 */
export interface AllWorkspaces {
  type: 'AllWorkspaces'
}

/**
 * Opportunities in a specific workspace.
 */
export interface SpecificWorkspace {
  type: 'SpecificWorkspace'
  workspace: Workspace
}

/**
 * Opportunity sorting type.
 */
export type OpportunitySort = DefaultSort | FieldSort

/**
 * Sort direction.
 */
export const enum SortDirection {
  Ascending,
  Descending,
}

/**
 * Default sort.
 */
export interface DefaultSort {
  type: 'Default'
}

/**
 * Field sort.
 */
export interface FieldSort {
  type: 'Field'
  field: OpportunitySortingField
  direction: SortDirection
}

/**
 * Opportunity search filter.
 */
export interface OpportunitySearchFilter {
  searchText?: string
  workspace: Workspace
  sort: OpportunitySort
}

export const OpportunitySearchFilter = {
  /**
   * An empty search filter.
   */
  none: {
    searchText: '',
    workspace: Workspace.none,
    sort: { type: 'Default' },
  } as OpportunitySearchFilter,

  /**
   * Whether the specified search filter is considered empty.
   */
  isNone: (searchFilter: OpportunitySearchFilter) =>
    OpportunitySearchFilter.equals(
      omit(OpportunitySearchFilter.none, 'sort'),
      omit(searchFilter, 'sort'),
    ),

  /**
   * Whether 2 search filters are considered equal.
   */
  equals: (
    left: Partial<OpportunitySearchFilter>,
    right: Partial<OpportunitySearchFilter>,
  ) => areDeepEqual(left, right),
}

/**
 * Search result.
 */
export interface OpportunitySearchResult {
  /**
   * The search filter that was used.
   */
  searchFilter: OpportunitySearchFilter
  /**
   * Whether any search filters were applied.
   */
  filtersApplied: boolean
  /**
   * The total number of search results (not just what was returned).
   */
  total: number
  /**
   * The current list of opportunities contained in the searcher.
   */
  readonly opportunities: readonly Opportunity[]
}

/**
 * Whether the change is immediate or debounced.
 */
export const enum ChangeScheduling {
  Immediate,
  Debounced,
}

/**
 * Search text change.
 */
export interface SearchTextChange {
  scheduling: ChangeScheduling
  value: string
}

/**
 * Current state of searching.
 */
export const enum SearchState {
  /**
   * The initial search is currently running.
   */
  InitialSearchRunning,
  /**
   * The search returned something.
   */
  Some,
  /**
   * The search returned an empty result, but
   * there was a filter applied.
   */
  EmptyButFilterApplied,
  /**
   * The search returned an empty result
   * and no filters were applied.
   */
  CompletelyEmpty,
}
